import React, { useRef, useEffect } from 'react';

function getMonthDates(selectedDate) {
  // Use local timezone for all date calculations
  let date;
  if (selectedDate) {
    // Parse date in local timezone to avoid UTC shift
    const [year, month, day] = selectedDate.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date();
  }
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dates = [];
  for (let d = 1; d <= daysInMonth; d++) {
    // Construct date in local time
    const dt = new Date(year, month, d, 0, 0, 0, 0);
    const day = dt.toLocaleDateString(undefined, { weekday: 'short' });
    // Use local date string (not UTC)
    const localIso = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
    dates.push({
      key: `${year}-${month + 1}-${d}`,
      label: day,
      num: d,
      iso: localIso,
    });
  }
  return dates;
}

const DatePicker = ({ selectedDate, onSelect }) => {
  const monthDates = getMonthDates(selectedDate);
  const scrollRef = useRef();
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftPos = useRef(0);  // Scroll navigation functions
  const scrollToLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollToRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Scroll to selected date or today
  // Scroll to selected date or today (centered)
useEffect(() => {
  if (!scrollRef.current) return;

  const container = scrollRef.current;
  const today = new Date();
  const todayIso = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  const effectiveSelectedDate = selectedDate || todayIso;

  const idx = monthDates.findIndex(d => d.iso === effectiveSelectedDate);
  const el = container.children[idx];

  if (el) {
    const containerWidth = container.offsetWidth;
    const elLeft = el.offsetLeft;
    const elWidth = el.offsetWidth;
    const scrollLeft = elLeft - (containerWidth / 2) + (elWidth / 2);
    container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }
}, [selectedDate, monthDates]);

  // Mouse/touch drag to scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    
    const onMouseDown = (e) => {
      isDragging.current = true;
      startX.current = e.pageX - el.offsetLeft;
      scrollLeftPos.current = el.scrollLeft;
      el.style.cursor = 'grabbing';
    };
    
    const onMouseLeave = () => {
      isDragging.current = false;
      el.style.cursor = 'grab';
    };
    
    const onMouseUp = () => {
      isDragging.current = false;
      el.style.cursor = 'grab';
    };
    
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX.current) * 1.5; // scroll-fast
      el.scrollLeft = scrollLeftPos.current - walk;
    };
    
    // Touch events
    const onTouchStart = (e) => {
      isDragging.current = true;
      startX.current = e.touches[0].pageX - el.offsetLeft;
      scrollLeftPos.current = el.scrollLeft;
    };
    
    const onTouchEnd = () => {
      isDragging.current = false;
    };
    
    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      const x = e.touches[0].pageX - el.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      el.scrollLeft = scrollLeftPos.current - walk;
    };
    
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('touchstart', onTouchStart);
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchmove', onTouchMove);
    el.style.cursor = 'grab';
      return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [monthDates]);

  return (
    <div className="relative mb-6">
      {/* Background container with glassmorphism effect */}
      <div className="relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-xl border border-white/20 shadow-2xl shadow-gray-200/50">
        {/* Gradient overlay - Green theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/50 via-green-50/30 to-teal-50/50 pointer-events-none"></div>        {/* Left scroll arrow */}
        <button 
          onClick={scrollToLeft}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 z-40 w-8 h-8 bg-emerald-500 border-2 border-white rounded-full shadow-lg hover:bg-emerald-600 hover:shadow-xl transition-all duration-300 flex items-center justify-center group active:scale-95"
          style={{ boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)' }}
        >
          <svg className="w-4 h-4 text-white group-hover:text-emerald-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Right scroll arrow */}
        <button 
          onClick={scrollToRight}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 z-40 w-8 h-8 bg-emerald-500 border-2 border-white rounded-full shadow-lg hover:bg-emerald-600 hover:shadow-xl transition-all duration-300 flex items-center justify-center group active:scale-95"
          style={{ boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)' }}
        >
          <svg className="w-4 h-4 text-white group-hover:text-emerald-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
          </svg>
        </button>{/* Date picker container */}
        <div
          className="relative flex flex-nowrap items-center justify-start gap-1.5 pl-14 pr-14 py-3 overflow-x-auto min-w-full"
          style={{ 
            minHeight: 75,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
          ref={scrollRef}
        >
          {monthDates.map(date => {
            const isSelected = date.iso === (selectedDate || (new Date().getFullYear() + '-' + String(new Date().getMonth() + 1).padStart(2, '0') + '-' + String(new Date().getDate()).padStart(2, '0')));
            const isToday = date.iso === (new Date().getFullYear() + '-' + String(new Date().getMonth() + 1).padStart(2, '0') + '-' + String(new Date().getDate()).padStart(2, '0'));
            
            return (              <button
                key={date.key}
                className={`
                  relative flex flex-col items-center justify-center 
                  min-w-[52px] h-16 px-3 py-2
                  rounded-xl border transition-all duration-500 ease-out
                  transform hover:scale-105 hover:-translate-y-1
                  focus:outline-none focus:ring-3 focus:ring-emerald-400/30
                  active:scale-95 active:transition-transform active:duration-100
                  group                  ${isSelected 
                    ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-600 border-emerald-400/50 text-white shadow-lg shadow-emerald-500/30 scale-105 -translate-y-1' 
                    : isToday 
                    ? 'bg-gradient-to-br from-emerald-400/10 via-emerald-50 to-green-50 border-emerald-300/60 text-emerald-700 shadow-md shadow-emerald-200/40' 
                    : 'bg-white/60 backdrop-blur border-gray-200/60 text-gray-700 hover:bg-white/80 hover:border-emerald-300/70 hover:shadow-md hover:shadow-emerald-200/30'
                  }
                `}
                onClick={() => onSelect(date.iso)}
                aria-pressed={isSelected}
                aria-label={`Select ${date.label}, ${date.num}`}
              >                {/* Animated background highlight for selected state */}
                {isSelected && (
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent opacity-50"></div>
                )}
                
                {/* Day label */}
                <span className={`
                  relative text-xs font-medium tracking-wide uppercase transition-all duration-300
                  ${isSelected 
                    ? 'text-emerald-100' 
                    : isToday 
                    ? 'text-emerald-600' 
                    : 'text-gray-500 group-hover:text-emerald-600'
                  }
                `}>
                  {date.label}
                </span>
                
                {/* Date number */}
                <span className={`
                  relative text-lg font-bold transition-all duration-300 mt-0.5
                  ${isSelected 
                    ? 'text-white' 
                    : isToday 
                    ? 'text-emerald-700' 
                    : 'text-gray-800 group-hover:text-emerald-700'
                  }
                `}>
                  {date.num}
                </span>
                
                {/* Today indicator dot */}
                {isToday && !isSelected && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                )}
                
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-white/60 rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>        
        {/* Subtle scroll indicators */}
        <div className="absolute left-14 top-0 bottom-0 w-6 bg-gradient-to-r from-white/40 to-transparent pointer-events-none"></div>
        <div className="absolute right-14 top-0 bottom-0 w-6 bg-gradient-to-l from-white/40 to-transparent pointer-events-none"></div>

      </div>
    </div>
  );
};

export default DatePicker;
